import {
  ASSIGNEE_KIND_AGENT,
  ASSIGNEE_KIND_CHATBOT,
  ASSIGNEE_KIND_QUEUE,
  ASSIGNEE_KIND_USER,
  buildAssigneeOption,
  getAgentId,
  isChatBotUser,
} from "./assigneeTransferField";
import { normalizeLoggedUsersFallback } from "./loggedUsersFallback";

/** Converte handoff_target da skill em seleção do AssigneeTransferField. */
export function handoffTargetToSelection(handoff = {}, { users = [], agents = [], labels = {} } = {}) {
  const type = String(handoff?.type || "").trim().toLowerCase();
  const rawId = handoff?.id != null ? String(handoff.id).trim() : "";

  if (type === "queue" || type === "fila" || type === "fila_humano") {
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_QUEUE,
        id: "fila_humano",
        label: labels.queue || "Fila de atendimento humano",
      }),
    ].filter(Boolean);
  }

  if (!rawId) return [];

  if (type === "agent") {
    const agent = (agents || []).find((a) => String(getAgentId(a)) === rawId);
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_AGENT,
        id: rawId,
        label: agent?.name ?? agent?.nome ?? `Agente #${rawId}`,
        raw: agent,
      }),
    ].filter(Boolean);
  }

  if (type === "chatbot") {
    const numId = Number(rawId);
    const user = (users || []).find((u) => Number(u.id) === numId);
    const name = user?.nome ?? user?.name ?? `ChatBot #${rawId}`;
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_CHATBOT,
        id: numId,
        label: name.endsWith("(ChatBot)") ? name : `${name} (ChatBot)`,
        raw: user,
      }),
    ].filter(Boolean);
  }

  if (type === "human") {
    const ids = rawId
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return ids
      .map((id) => {
        const numId = Number(id);
        if (!Number.isFinite(numId) || numId <= 0) return null;
        const user = (users || []).find((u) => Number(u.id) === numId);
        if (user && isChatBotUser(user)) return null;
        return buildAssigneeOption({
          kind: ASSIGNEE_KIND_USER,
          id: numId,
          label: user?.nome ?? user?.name ?? `Usuário #${numId}`,
          raw: user,
        });
      })
      .filter(Boolean);
  }

  return [];
}

/** Converte seleção unificada para handoff_target persistido na skill. */
export function selectionToHandoffTarget(
  selection = [],
  {
    logged_users_only = false,
    logged_users_fallback = null,
    tipo_id = null,
    tipo1_id = null,
    tipo2_id = null,
    tipo3_id = null,
  } = {}
) {
  const list = Array.isArray(selection) ? selection.filter(Boolean) : [];
  const toTipo = (v) => (v != null && v !== "" ? Number(v) : null);
  const base = {
    type: "queue",
    id: "",
    tipo_id: toTipo(tipo_id),
    tipo1_id: toTipo(tipo1_id),
    tipo2_id: toTipo(tipo2_id),
    tipo3_id: toTipo(tipo3_id),
    logged_users_only: logged_users_only === true,
    logged_users_fallback:
      logged_users_only === true ? normalizeLoggedUsersFallback(logged_users_fallback) : null,
  };

  // Vazio = fila humana (padrão seguro do ticket_seller / complaints).
  if (!list.length) return { ...base, type: "queue", id: "", logged_users_only: false, logged_users_fallback: null };

  const first = list[0];
  if (first.kind === ASSIGNEE_KIND_QUEUE) {
    return { ...base, type: "queue", id: "", logged_users_only: false, logged_users_fallback: null };
  }
  if (first.kind === ASSIGNEE_KIND_AGENT) {
    return { ...base, type: "agent", id: String(first.id), logged_users_only: false, logged_users_fallback: null };
  }
  if (first.kind === ASSIGNEE_KIND_CHATBOT) {
    return { ...base, type: "chatbot", id: String(first.id), logged_users_only: false, logged_users_fallback: null };
  }

  const humanIds = list
    .filter((item) => item.kind === ASSIGNEE_KIND_USER)
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!humanIds.length) return { ...base, type: "queue", id: "", logged_users_only: false, logged_users_fallback: null };

  return {
    ...base,
    type: "human",
    id: humanIds.length === 1 ? String(humanIds[0]) : humanIds.join(","),
    logged_users_only: logged_users_only === true,
  };
}

/** Valores legados para AssigneeTransferField (somente leitura inicial). */
export function handoffTargetToLegacyValues(handoff = {}, ctx = {}) {
  const selection = handoffTargetToSelection(handoff, ctx);
  if (!selection.length) {
    return { transferirusuario: "", transferirparaagente: "" };
  }
  if (selection[0].kind === ASSIGNEE_KIND_QUEUE) {
    // Legado não modela fila; consumidores devem preferir `handoffTargetToSelection`.
    return { transferirusuario: "", transferirparaagente: "" };
  }
  if (selection[0].kind === ASSIGNEE_KIND_AGENT) {
    return { transferirusuario: "", transferirparaagente: String(selection[0].id) };
  }
  const ids = selection
    .filter((item) => item.kind === ASSIGNEE_KIND_USER || item.kind === ASSIGNEE_KIND_CHATBOT)
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id) && id > 0);
  const transferirusuario = ids.length <= 1 ? ids[0] ?? "" : ids;
  return { transferirusuario, transferirparaagente: "" };
}
